/**
 * Client-side sentence diff used by the "Improve my rules" cards
 * (BUSINESS_RULE_GENERATION_HARDENING_TDD §6.4): a refined option keeps the admin's existing
 * sentences verbatim, so any sentence that is NOT one of the existing sentences is new and gets
 * bold. Sentence-by-sentence comparison, no server round trip, no fuzzy matching.
 */

export interface RuleSegment {
  text: string;
  /** True when the segment is a sentence that is not matched by a sentence in the baseline text. */
  isNew: boolean;
}

// A sentence = run of non-terminator chars plus its terminator(s); newline runs are kept as their
// own segments so paragraph breaks survive rendering (cards use white-space: pre-line).
//
// PR #684 review (L2) — a bare `.` inside the content run used to end the sentence there too,
// so a number like "5.5 days" fragmented into "5." + "5 days ..." and got bolded as "new" along
// with the real next sentence. A real end-of-sentence `.` (not between two digit runs) still ends
// the segment as before. Abbreviations ("e.g.", "Mr.") aren't addressed — that needs a dictionary,
// a much larger change for a cosmetic, Low-severity issue.
//
// PR #685 review (L1, round 1) — the first version used a `\d+\.\d+` alternative, which
// backtracks quadratically on a long digit run (measured: ~230ms for 20,000 digits; this runs on
// every card render).
//
// PR #685 review (L1, round 2) — the lookbehind/lookahead replacement (`(?<=\d)\.(?=\d)`) fixed
// the backtracking but is a syntax error on Safari before 16.4 (lookbehind wasn't supported), and
// a regex literal that fails to parse breaks the whole module — this is imported by
// DescriptionCards, so the add/edit-channel UI would fail to load there. vite.config.ts sets no
// build target/browserslist, so nothing transpiles it away. `\d\.(?=\d)` gets the same linear,
// no-backtracking behavior with a lookahead only (broadly supported since ES2018, years before
// lookbehind): consume a digit and its following decimal point together, but only when another
// digit comes right after.
const SEGMENT_PATTERN = /\n+|(?:\d\.(?=\d)|[^.!?\n])+(?:[.!?]+|(?=\n)|$)\s*/g;

const normalize = (value: string): string => value.replace(/\s+/g, ' ').trim().toLowerCase();

const splitSentences = (text: string): string[] =>
  (text.match(SEGMENT_PATTERN) ?? []).map(normalize).filter((sentence) => sentence.length > 0);

/**
 * Split `candidate` into render segments, flagging sentences that are not in `baseline`.
 *
 * Matching is whole sentence to whole sentence (a sentence that merely appears inside a longer
 * baseline sentence is new), and each baseline sentence can match only as many candidate sentences
 * as it occurs times in the baseline — so a sentence the model repeats is bold from its second
 * appearance. Whitespace-only and newline segments are never flagged. An empty baseline flags
 * nothing: there is nothing to compare against, so bolding everything would be noise.
 */
export function diffRuleSentences(candidate: string, baseline: string): RuleSegment[] {
  const remaining = new Map<string, number>();
  for (const sentence of splitSentences(baseline)) {
    remaining.set(sentence, (remaining.get(sentence) ?? 0) + 1);
  }
  const hasBaseline = remaining.size > 0;

  return (candidate.match(SEGMENT_PATTERN) ?? []).map((text) => {
    const normalized = normalize(text);
    if (!hasBaseline || normalized.length === 0) {
      return { text, isNew: false };
    }
    const available = remaining.get(normalized) ?? 0;
    if (available > 0) {
      remaining.set(normalized, available - 1);
      return { text, isNew: false };
    }
    return { text, isNew: true };
  });
}
